import { post, get } from 'aws-amplify/api';
import { logger } from 'src/logger';
import { SearchRequestBody, FintechPapiResponse, FintechPapiEmployeeResponse, FintechPapiPersonInfo } from './interfaces';

/**
 * Checks if an error is an AbortError and throws if so.
 * This allows proper cancellation propagation.
 */
const checkAbortSignal = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    const error = new Error('Request was cancelled');
    error.name = 'AbortError';
    throw error;
  }
};

/**
 * Search employees using the FINTECH_PAPI /v1/search/advanced endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param searchQuery - The search query string (minimum 2 characters)
 * @param maxResults - Maximum number of results to return (default: 10)
 * @param nextToken - Optional token for pagination
 * @returns A promise that resolves to the raw FINTECH_PAPI response
 */
export const searchEmployeesFromFintechPapi = async (
  searchQuery: string,
  maxResults: number = 10,
  nextToken?: string
): Promise<FintechPapiResponse> => {
  try {
    const path = `/v1/search/advanced`;
    var requestBody: SearchRequestBody = {
      name: searchQuery.toLowerCase(),
      activeOnly: true,
      jobLevel: {
        min: 4
      },
      size: maxResults
    };

    // Add nextToken to request if provided
    if (nextToken) {
      requestBody.nextToken = nextToken;
    }

    const response = await post({
      apiName: 'FINTECH_PAPI',
      path,
      options: {
        body: requestBody as any,
        queryParams: {
          expand: 'job'
        }
      }
    }).response;

    const responseBody = await response.body.json();
    return responseBody as FintechPapiResponse;
  } catch (error) {
    logger.warn('FINTECH_PAPI search failed', { error, searchQuery });
    return {
      employees: [],
      totalCount: 0,
      hasMore: false
    };
  }
};

/**
 * Get employee by legacy employee ID using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param legacyEmployeeId - The legacy employee ID (e.g., "xxxxx")
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeByLegacyId = async (legacyEmployeeId: string): Promise<FintechPapiEmployeeResponse> => {
  try {
    const path = `/v1/employee/employee-id/${legacyEmployeeId}`;

    const response = await get({
      apiName: 'FINTECH_PAPI',
      path
    }).response;

    const responseBody = await response.body.json();
    return responseBody as FintechPapiEmployeeResponse;
  } catch (error) {
    logger.warn('FINTECH_PAPI getEmployeeByLegacyId failed', { error, legacyEmployeeId });
    return {
      employee: undefined
    };
  }
};

/**
 * Get employee by UUID employee ID using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param employeeId - The legacy employee ID (e.g., "xxxxxx-xxxx-xxx-xxx-xxxxxxxxxx")
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeById = async (employeeId: string): Promise<FintechPapiEmployeeResponse> => {
  try {
    const path = `/v1/employee/${employeeId}`;

    const response = await get({
      apiName: 'FINTECH_PAPI',
      path,
      options: {
        queryParams: {
          expand: 'job'
        }
      }
    }).response;

    const responseBody = await response.body.json();
    return responseBody as FintechPapiEmployeeResponse;
  } catch (error) {
    logger.warn('FINTECH_PAPI getEmployeeById failed', { error, employeeId });
    return {
      employee: undefined
    };
  }
};

/**
 * Get employee by alias using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param login - Alias of an employee (e.g., "johndoe")
 * @param expand - Optional expand parameter (e.g., 'job', 'direct-reports', 'job,direct-reports')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeByLogin = async (login: string, expand: string = 'job', signal?: AbortSignal): Promise<FintechPapiEmployeeResponse> => {
  try {
    // Check if already cancelled before making request
    checkAbortSignal(signal);

    const path = `/v1/employee/login/${login.toLowerCase()}`;

    const apiOperation = get({
      apiName: 'FINTECH_PAPI',
      path,
      options: {
        queryParams: {
          expand
        }
      }
    });

    // If signal is provided, set up cancellation
    if (signal) {
      signal.addEventListener('abort', () => apiOperation.cancel(), { once: true });
    }

    const response = await apiOperation.response;

    // Check again after response in case cancelled during request
    checkAbortSignal(signal);

    const responseBody = await response.body.json();
    return responseBody as FintechPapiEmployeeResponse;
  } catch (error) {
    // Re-throw abort errors to allow proper cancellation propagation
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.warn('FINTECH_PAPI getEmployeeByLogin failed', { error, login });
    return {
      employee: undefined
    };
  }
};

/**
 * Counts all reports under a leader recursively using PAPI direct-reports endpoint.
 * Useful for fail-fast validation before fetching all employee data.
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Total count of all reports (direct + indirect) under the leader
 */
export const countAllReportsUnderLeader = async (login: string, signal?: AbortSignal): Promise<number> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return 0;
    }

    let totalCount = employee.directReports.length;

    // Check if cancelled before recursive calls
    checkAbortSignal(signal);

    // Recursively count reports for managers among direct reports
    const managerReports = employee.directReports.filter((report: FintechPapiPersonInfo) => report.isManager);

    const childCounts = await Promise.all(
      managerReports.map((manager: FintechPapiPersonInfo) => (manager.login ? countAllReportsUnderLeader(manager.login, signal) : Promise.resolve(0)))
    );

    totalCount += childCounts.reduce((sum, count) => sum + count, 0);

    return totalCount;
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to count all reports under leader', { error, login });
    throw error;
  }
};

/**
 * Fetches all employee logins under a leader recursively using PAPI direct-reports endpoint.
 * Returns a flat array of all employee logins (excluding the leader).
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of all employee logins under the leader
 */
export const fetchAllReportsRecursively = async (login: string, signal?: AbortSignal): Promise<string[]> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return [];
    }

    const directReportLogins = employee.directReports
      .map((report: FintechPapiPersonInfo) => report.login)
      .filter((reportLogin: string | undefined): reportLogin is string => !!reportLogin);

    // Check if cancelled before recursive calls
    checkAbortSignal(signal);

    // Recursively fetch reports for managers among direct reports
    const managerReports = employee.directReports.filter((report: FintechPapiPersonInfo) => report.isManager);

    const childReports = await Promise.all(
      managerReports.map((manager: FintechPapiPersonInfo) =>
        manager.login ? fetchAllReportsRecursively(manager.login, signal) : Promise.resolve([])
      )
    );

    const allChildLogins = childReports.flat();

    return [...directReportLogins, ...allChildLogins];
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to fetch all reports under leader recursively', { error, login });
    throw error;
  }
};

/**
 * Fetches direct reports for a leader using PAPI direct-reports endpoint.
 * Returns only the immediate direct reports (no recursion).
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of direct report logins
 */
export const fetchDirectReports = async (login: string, signal?: AbortSignal): Promise<string[]> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return [];
    }

    return employee.directReports
      .map((report: FintechPapiPersonInfo) => report.login)
      .filter((reportLogin: string | undefined): reportLogin is string => !!reportLogin);
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to fetch direct reports', { error, login });
    throw error;
  }
};
